import ExcelJS from 'exceljs'
import { detectWorkbookFormat, importFormats } from 'features/RateCodes/formatRegistry'
import {
  readHotelNameFromSheet,
  readLighthouseRows
} from 'features/RateCodes/lighthouseFormat'
import { readNativeRows } from 'features/RateCodes/nativeFormat'
import {
  rateCodeCategoryKeyByLabel,
  rateCodeCategoryNames
} from 'features/RateCodes/rateCodeCategories'

// Enum: key → Excel label
export const CATEGORY_LABELS = rateCodeCategoryNames

// Reverse lookup: Excel label → enum key
const LABEL_TO_KEY = rateCodeCategoryKeyByLabel

export function rateCodesMatch(a, b) {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

/**
 * Merges imported rate codes into UI state.
 * Matches booking rate codes case-insensitively so Excel casing variants still categorize.
 */
export function mergeImportedRateCodes(rateState, importedRateCodes, isSubsidiaryLevel) {
  const updatedRateState = { ...rateState }
  const newChanges = []

  Object.keys(importedRateCodes).forEach(category => {
    if (category === 'Unknown') {
      return
    }

    const ratesToAdd = []

    // Built once per category: updatedRateState only changes between categories, and
    // rebuilding it per row makes large imports quadratic.
    const allCategorized = Object.entries(updatedRateState)
      .filter(([key]) => key !== 'Unknown')
      .flatMap(([, rates]) => rates || [])

    importedRateCodes[category].forEach(rate => {
      const matchingUnknown = (updatedRateState.Unknown || []).filter(unknownRate =>
        rateCodesMatch(unknownRate.code, rate.code)
      )

      if (matchingUnknown.length > 0) {
        matchingUnknown.forEach(unknownRate => {
          if (!ratesToAdd.some(r => r.code === unknownRate.code)) {
            ratesToAdd.push({
              ...unknownRate,
              isImported: true
            })
          }
        })
        return
      }

      if (
        !allCategorized.some(existingRate => rateCodesMatch(existingRate.code, rate.code))
      ) {
        ratesToAdd.push({
          ...rate,
          isImported: true
        })
      }
    })

    if (ratesToAdd.length === 0) {
      return
    }

    const existingCategory = updatedRateState[category] || []
    const mergedCategory = [...existingCategory]

    ratesToAdd.forEach(newRate => {
      const existingIndex = mergedCategory.findIndex(
        existingRate => existingRate.code === newRate.code
      )

      if (existingIndex >= 0) {
        mergedCategory[existingIndex] = newRate
      } else {
        mergedCategory.push(newRate)
      }
    })

    updatedRateState[category] = mergedCategory

    if (updatedRateState.Unknown) {
      updatedRateState.Unknown = updatedRateState.Unknown.filter(
        unknownRate => !ratesToAdd.some(r => rateCodesMatch(r.code, unknownRate.code))
      )
    }

    ratesToAdd.forEach(rate => {
      newChanges.push({
        isSubsidiaryLevel,
        code: rate.code,
        kind: category
      })
    })
  })

  return { updatedRateState, newChanges }
}

const ACCEPTED_EXTENSION = '.xlsx'

const UNRECOGNIZED_FORMAT_ERROR =
  'This file is not a format we can import. A Directful export has "Rate code" and "Category" headers on the first row; a Lighthouse export has "Value" and "Mapping" headers.'

function validateFileName(fileName) {
  if (!fileName.toLowerCase().endsWith(ACCEPTED_EXTENSION)) {
    // ExcelJS cannot read the legacy binary .xls format, so accepting it here only defers
    // the failure to an unreadable parse error.
    return `Invalid file type. Expected an Excel ${ACCEPTED_EXTENSION} file, got: ${fileName}. Legacy .xls files must be saved as ${ACCEPTED_EXTENSION} first.`
  }

  return null
}

async function readWorkbook(file) {
  const arrayBuffer = await file.arrayBuffer()
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(arrayBuffer)
  return workbook
}

function validateNativeRows(workbook, detection, rows) {
  const errors = []
  const warnings = []

  if (!workbook.getWorksheet('Rates')) {
    warnings.push(
      `Sheet named "Rates" not found. Using sheet: "${detection.sheet.name}"`
    )
  }

  const invalidCategories = new Set()
  const emptyCodeRows = []

  rows.forEach(row => {
    if (!row.code) {
      emptyCodeRows.push(row.rowNumber)
    }

    if (row.categoryLabel && !Object.values(CATEGORY_LABELS).includes(row.categoryLabel)) {
      invalidCategories.add(row.categoryLabel)
    }
  })

  // Report empty code rows
  if (emptyCodeRows.length > 0) {
    if (emptyCodeRows.length <= 5) {
      warnings.push(`Empty code values found in rows: ${emptyCodeRows.join(', ')}`)
    } else {
      warnings.push(
        `Empty code values found in ${emptyCodeRows.length} rows (first 5: ${emptyCodeRows
          .slice(0, 5)
          .join(', ')}...)`
      )
    }
  }

  // Report invalid categories as warnings (not errors) - they will be filtered out
  if (invalidCategories.size > 0) {
    const invalidList = Array.from(invalidCategories).slice(0, 5).join(', ')
    const suffix = invalidCategories.size > 5 ? '...' : ''
    warnings.push(
      `Unknown category values found: ${invalidList}${suffix}. These rows will be available for download.`
    )
  }

  // Check for minimum data
  if (rows.length === 0) {
    errors.push('Excel file contains no data rows (only header found)')
  }

  return { errors, warnings, rowCount: rows.length }
}

/**
 * Validates an Excel file for rate code import.
 * Checks the file is a format we recognize and, for our own export, that its category values
 * are ones we can act on.
 *
 * @param {File} file - The Excel file to validate
 * @returns {Promise<{valid: boolean, errors: string[], warnings: string[], rowCount: number}>}
 */
export async function validateExcelFile(file) {
  const errors = []
  const warnings = []
  let rowCount = 0

  // Check file extension
  const fileName = file.name || ''
  const extensionError = validateFileName(fileName)

  if (extensionError) {
    errors.push(extensionError)
    return { valid: false, errors, warnings, rowCount }
  }

  try {
    const workbook = await readWorkbook(file)

    // Check for worksheets
    if (!workbook.worksheets || workbook.worksheets.length === 0) {
      errors.push('Excel file contains no worksheets')
      return { valid: false, errors, warnings, rowCount }
    }

    const { format, detection } = detectWorkbookFormat(workbook)

    if (format === importFormats.unknown) {
      errors.push(UNRECOGNIZED_FORMAT_ERROR)
      return { valid: false, errors, warnings, rowCount }
    }

    if (format === importFormats.lighthouse) {
      rowCount = readLighthouseRows(detection).length

      if (rowCount === 0) {
        errors.push('Lighthouse mapping file contains no rate code rows')
      }

      return { valid: errors.length === 0, errors, warnings, rowCount }
    }

    const validation = validateNativeRows(workbook, detection, readNativeRows(detection))

    errors.push(...validation.errors)
    warnings.push(...validation.warnings)
    rowCount = validation.rowCount
  } catch (error) {
    errors.push(`Failed to parse Excel file: ${error.message}`)
    return { valid: false, errors, warnings, rowCount }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    rowCount
  }
}

/**
 * Groups rows carrying a category label into the buckets the page renders.
 * Used for both our own export and a categorized Lighthouse file, so a Lighthouse import lands
 * exactly like a workbook a CSM filled in by hand.
 *
 * Last row wins for duplicate codes (case-insensitive), matching backend save behavior. A row
 * left Uncategorized or carrying a label we do not know overrides an earlier categorized row
 * for the same code, so the file's final word is what counts.
 *
 * Output:
 * {
 *   buckets: { OTA: [{code}], ... },
 *   unknownRows: [{ code, category, rowNumber }]
 * }
 */
export function bucketsFromCategorizedRows(rows) {
  const buckets = {}
  const unknownRows = []

  // eslint-disable-next-line no-unused-vars
  const { Unknown, ...categoryLabelsExceptUnknown } = CATEGORY_LABELS

  for (const key of Object.keys(categoryLabelsExceptUnknown)) {
    buckets[key] = []
  }

  const codeAssignments = new Map()

  ;(rows || []).forEach(row => {
    const code = row.code ? String(row.code).trim() : ''

    if (!code) {
      return
    }

    const categoryLabel = row.categoryLabel ? String(row.categoryLabel).trim() : ''

    if (categoryLabel === CATEGORY_LABELS.Unknown) {
      codeAssignments.delete(code.toLowerCase())
      return
    }

    const bucketKey = LABEL_TO_KEY[categoryLabel]

    if (!bucketKey) {
      codeAssignments.delete(code.toLowerCase())

      unknownRows.push({
        code,
        description: row.description,
        category: categoryLabel || '(empty)',
        rowNumber: row.rowNumber
      })
      return
    }

    codeAssignments.set(code.toLowerCase(), { bucketKey, code })
  })

  codeAssignments.forEach(({ bucketKey, code }) => {
    buckets[bucketKey].push({ code })
  })

  return { buckets, unknownRows }
}

/**
 * Reads uploaded Excel file and returns grouped bucket JSON.
 * Output:
 * {
 *   buckets: { OTA: [{code}], ... },
 *   unknownRows: [{ code, category, rowNumber }]
 * }
 */
export async function parseExcelToRateCodeBuckets(file) {
  const workbook = await readWorkbook(file)

  const detection = detectWorkbookFormat(workbook)

  if (detection.format !== importFormats.native) {
    throw new Error('Could not find our own rate codes export in Excel')
  }

  return bucketsFromCategorizedRows(readNativeRows(detection.detection))
}

function escapeCsvValue(value) {
  return `"${String(value === null || value === undefined ? '' : value).replace(
    /"/g,
    '""'
  )}"`
}

/**
 * Generates a CSV string from reported rows
 * @param {Array<{code: string, category: string, description?: string, rowNumber: number}>} unknownRows
 * @returns {string} CSV content
 */
export function generateUnknownRowsCsv(unknownRows) {
  const header = 'Code,Description,Category,Original Row Number'
  const rows = unknownRows.map(
    row =>
      `${escapeCsvValue(row.code)},${escapeCsvValue(row.description)},${escapeCsvValue(
        row.category
      )},${row.rowNumber}`
  )
  return [header, ...rows].join('\n')
}

/**
 * Creates a downloadable CSV blob URL from unknown rows
 * @param {Array<{code: string, category: string, rowNumber: number}>} unknownRows
 * @returns {string} Blob URL for download
 */
export function createUnknownRowsCsvDownloadUrl(unknownRows) {
  const csvContent = generateUnknownRowsCsv(unknownRows)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  return URL.createObjectURL(blob)
}

/**
 * Groups reported rows by their source category, most frequent first.
 * @returns {Array<{category: string, count: number}>}
 */
export function summarizeRowsByCategory(rows) {
  const countsByCategory = new Map()

  ;(rows || []).forEach(row => {
    const category = row.category || '(empty)'
    countsByCategory.set(category, (countsByCategory.get(category) || 0) + 1)
  })

  return Array.from(countsByCategory.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count)
}

function emptyParseResult(format, errors) {
  return {
    format,
    valid: false,
    errors,
    warnings: [],
    rowCount: 0,
    buckets: {},
    unknownRows: [],
    lighthouseRows: [],
    hotelNameFromFile: ''
  }
}

/**
 * Single entry point for the import dialog. Reads the workbook once and asks the fingerprint
 * registry what it is, so the dialog knows up front whether it is holding our own export, a
 * Lighthouse export, or a file we cannot import.
 *
 * A native file arrives categorized in buckets. A Lighthouse file arrives as rows still to be
 * categorized, which the dialog sends to the Data Processor.
 *
 * @param {File} file
 * @returns {Promise<{
 *   format: 'native'|'lighthouse'|'unknown',
 *   valid: boolean,
 *   errors: string[],
 *   warnings: string[],
 *   rowCount: number,
 *   buckets: Object,
 *   unknownRows: Array,
 *   lighthouseRows: Array,
 *   hotelNameFromFile: string
 * }>}
 */
export async function parseRateCodeWorkbook(file) {
  const fileName = file.name || ''
  const extensionError = validateFileName(fileName)

  if (extensionError) {
    return emptyParseResult(importFormats.unknown, [extensionError])
  }

  let workbook

  try {
    workbook = await readWorkbook(file)
  } catch (error) {
    return emptyParseResult(importFormats.unknown, [
      `Failed to parse Excel file: ${error.message}`
    ])
  }

  if (!workbook.worksheets || workbook.worksheets.length === 0) {
    return emptyParseResult(importFormats.unknown, ['Excel file contains no worksheets'])
  }

  const { format, detection } = detectWorkbookFormat(workbook)

  if (format === importFormats.lighthouse) {
    return parseLighthouseWorkbook(detection)
  }

  if (format === importFormats.native) {
    return parseNativeWorkbook(workbook, detection)
  }

  return emptyParseResult(importFormats.unknown, [UNRECOGNIZED_FORMAT_ERROR])
}

function parseLighthouseWorkbook(detection) {
  const errors = []

  let rows

  try {
    rows = readLighthouseRows(detection)
  } catch (error) {
    return emptyParseResult(importFormats.lighthouse, [
      `Failed to read the Lighthouse mapping sheet: ${error.message}`
    ])
  }

  if (rows.length === 0) {
    errors.push('Lighthouse mapping file contains no rate code rows')
  }

  return {
    format: importFormats.lighthouse,
    valid: errors.length === 0,
    errors,
    warnings: [],
    rowCount: rows.length,
    buckets: {},
    unknownRows: [],
    lighthouseRows: rows,
    hotelNameFromFile: readHotelNameFromSheet(detection.sheet, detection.headerRow)
  }
}

function parseNativeWorkbook(workbook, detection) {
  const rows = readNativeRows(detection)
  const validation = validateNativeRows(workbook, detection, rows)

  if (validation.errors.length > 0) {
    return {
      ...emptyParseResult(importFormats.native, validation.errors),
      warnings: validation.warnings,
      rowCount: validation.rowCount
    }
  }

  const parsed = bucketsFromCategorizedRows(rows)

  return {
    format: importFormats.native,
    valid: true,
    errors: [],
    warnings: validation.warnings,
    rowCount: validation.rowCount,
    buckets: parsed.buckets,
    unknownRows: parsed.unknownRows,
    lighthouseRows: [],
    hotelNameFromFile: ''
  }
}

export default parseExcelToRateCodeBuckets
